/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { ErrorMessage } from '@hookform/error-message';
import { cloneDeep } from 'lodash-es';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import Button from '@/components/elements/Button/BaseButton';
import Input from '@/components/elements/Form/Input';
import { MAX_BET_COUNT } from '@/config/constants/bet';
import { getOddsRange } from '@/lib/race';
import { BetDataList, BetDetail, Odds } from '@/modelTypes';

import {
  Form,
  FundSettingWrapper,
  HundredYenSpan,
  SettingName,
  ValidMsg,
} from '../style';

interface DistributeFundSettingProps {
  raceId: string;
  itemId?: string;
  odds: Odds;
  betDetails: BetDetail[];
  setBetDataList: React.Dispatch<React.SetStateAction<BetDataList>>;
  setOpenList: React.Dispatch<React.SetStateAction<string[]>>;
}

interface FormInput {
  fund: string;
}

const VALID_MSG1 = '投資金額配分は100円以上で入力してください。';
const VALID_MSG2 = '投資金額配分は半角数字のみで入力してください。';

export interface Bet {
  setNum: number;
  odds: number;
  ticketCount: number;
  dist: number;
}

export interface BetSet {
  [key: string]: number;
}

// 投資金額をどのように配分するか算出する
export const calcDistributeFund = (inputNumVal: number, bets: Bet[]) => {
  // 各票のオッズから逆数を計算し逆数の合計を算出
  const inverseSum = bets.reduce(
    (sum: number, bet: Bet) => sum + 1.0 / bet.odds,
    0,
  );

  // 購入口数に対して購入金額が下回っていた場合、配分率が低い方（オッズの高い方）から0口になるように配分
  if (bets.length >= inputNumVal) {
    bets.sort((a: Bet, b: Bet) => {
      return a.odds > b.odds ? 1 : -1;
    });
    for (let i = 0; i < inputNumVal; i++) {
      bets[i].ticketCount = 1;
    }
    return bets.reduce((betSet: BetSet, currentBet: Bet) => {
      betSet[currentBet.setNum] = currentBet.ticketCount;
      return betSet;
    }, {} as BetSet);
  }

  // 払戻平均額を計算
  const average = inputNumVal / inverseSum;

  // 払戻平均額をもとに各票ごとの購入口数を算出
  let ticketCountSum = 0;
  bets.forEach(bet => {
    const ticketCount = Math.ceil(average / bet.odds);
    bet.ticketCount = ticketCount < MAX_BET_COUNT ? ticketCount : MAX_BET_COUNT;
    ticketCountSum += bet.ticketCount;
  });

  // 投資金額との口数の差分算出
  let diff = ticketCountSum - inputNumVal;

  // 各票ごとの絶対値を取得
  bets.forEach(bet => {
    const adjust = diff > 0 ? -1 : 1;
    let dist;
    if (bet.ticketCount === 1 || bet.ticketCount >= MAX_BET_COUNT) {
      dist = -1;
    } else {
      dist = Math.abs(bet.odds * (bet.ticketCount + adjust) - average);
    }
    bet.dist = dist; // betモデルに入れておく
  });

  // 絶対値が少ない票から投資金額との差分を調整
  while (diff !== 0) {
    // 全ての買い目の口数がMAX_BETの値である場合、ループから抜ける
    if (bets.every(bet => bet.ticketCount === MAX_BET_COUNT)) break;

    bets.sort((a: Bet, b: Bet) => {
      // 第1ソート:絶対値が-1は順位を下げる
      if (a.dist === -1) return 1;
      if (b.dist === -1) return -1;
      // 第2ソート：絶対値が小さい方の順位をあげる
      if (a.dist > b.dist) return 1;
      if (a.dist < b.dist) return -1;
      // 第3ソート：オッズが大きい方の順位をあげる
      if (a.odds > b.odds) return 1;
      return -1;
    });

    const adjust = diff > 0 ? -1 : 1;

    bets[0].ticketCount += adjust;
    if (bets[0].ticketCount === 1 || bets[0].ticketCount >= MAX_BET_COUNT) {
      bets[0].dist = -1;
      bets[0].ticketCount =
        bets[0].ticketCount >= MAX_BET_COUNT
          ? MAX_BET_COUNT
          : bets[0].ticketCount;
    } else {
      bets[0].dist = Math.abs(
        bets[0].odds * (bets[0].ticketCount + adjust) - average,
      );
    }
    diff += adjust;
  }

  return bets.reduce((betSet: BetSet, currentBet: Bet) => {
    betSet[currentBet.setNum] = currentBet.ticketCount;
    return betSet;
  }, {} as BetSet);
};

const DistributeFundSetting: React.FC<DistributeFundSettingProps> = ({
  raceId,
  itemId,
  odds,
  betDetails,
  setBetDataList,
  setOpenList,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    reValidateMode: 'onSubmit',
  });

  const getBets = (_betDetails: BetDetail[]) => {
    const bets: Bet[] = [];
    _betDetails.forEach(bd => {
      bd.tickets.forEach(ticket => {
        if (!ticket.isRemoved) {
          const oddsRange = getOddsRange(odds, [ticket.setNum], bd.betType);
          bets.push({
            setNum: ticket.setNum,
            odds: oddsRange.min,
            ticketCount: 0,
            dist: 0,
          });
        }
      });
    });
    return bets;
  };

  const setCount = (_betDetails: BetDetail[], betSet: BetSet) => {
    _betDetails.forEach(bd => {
      bd.tickets.forEach(ticket => {
        if (!ticket.isRemoved) {
          ticket.count = betSet[ticket.setNum];
        }
      });
    });
  };

  const setFund = (data: FormInput) => {
    const betSet = calcDistributeFund(
      Number(data.fund),
      getBets(cloneDeep(betDetails)),
    );
    setBetDataList(prev => {
      const temp = { ...prev };
      if (itemId) {
        setCount([temp[raceId][itemId]], betSet);
        setOpenList(_prev => [..._prev, itemId]);
      } else {
        const ids: string[] = [];
        const bds: BetDetail[] = [];
        Object.keys(temp[raceId]).forEach(key => {
          ids.push(key);
          bds.push(temp[raceId][key]);
        });
        setCount(bds, betSet);
        setOpenList(_prev => [..._prev, ...ids]);
      }
      return temp;
    });
    toast.success('投資金額配分がセットされました');
  };

  return (
    <>
      <FundSettingWrapper>
        <SettingName>投資金額配分</SettingName>
        <Form onSubmit={handleSubmit(setFund)}>
          <Input
            type="tel"
            width="65px"
            height="40px"
            textAlign="right"
            hasError={Boolean(errors.fund)}
            {...register('fund', {
              required: {
                value: true,
                message: VALID_MSG1,
              },
              min: {
                value: 1,
                message: VALID_MSG1,
              },
              pattern: {
                value: /^\d+$/,
                message: VALID_MSG2,
              },
            })}
          />
          <HundredYenSpan>00円</HundredYenSpan>
          <Button
            bgColor="primary"
            size="xs"
            width="60px"
            height="40px"
            radius="m"
            type="submit"
            fontWeight="bold"
          >
            セット
          </Button>
        </Form>
      </FundSettingWrapper>
      <ErrorMessage
        errors={errors}
        name="fund"
        render={({ message }) => <ValidMsg>{message}</ValidMsg>}
      />
    </>
  );
};

export default DistributeFundSetting;
